import styled from '@emotion/styled';
import { useEventLogging } from '@innovamat/event-logging';
import {
  Button,
  IconButton,
  Typography,
  useDevice,
} from '@innovamat/glimmer-components';
import {
  LOGOUT_WEBVIEW,
  sendEventToWebview,
  useWebviewBridge,
} from '@innovamat/webview-bridge';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Status,
  usePasswordExpirationStatus,
} from '../hooks/use-password-expiration-status';
import { useUser } from '../providers';
import { useMySettings } from '../providers/my-settings-provider';

const Header = styled.div`
  border-bottom: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-subtle'].value};
`;

const HeaderContent = styled.div`
  display: flex;
  width: 100%;
  margin: 0 auto;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    padding: 0.5rem 1rem;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    margin: 1rem;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 22;
    background-color: ${({ theme }) =>
      theme.tokens.color.alias.cm.surface['surface-primary'].value};
  }
`;

const Title = styled(Typography.Body2)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

export function MySettingHeader(): JSX.Element {
  const { isMobile } = useDevice();
  const { t } = useTranslation();
  const { onClearUserSession } = useUser();
  const { status } = usePasswordExpirationStatus();
  const { onCancel, onSaveChanges, isSubmitEnabled, isLoading, onGoBack } =
    useMySettings();
  const navigate = useNavigate();
  const { setEventData } = useEventLogging();
  const { isInWebview } = useWebviewBridge();

  const drawerRoot = document.getElementById('drawer') || document.body;

  const isStatusExpired = status === Status.EXPIRED;

  const hideBackButton = isStatusExpired;

  const handleLogout = async () => {
    setEventData('sys_logout', {});

    onClearUserSession();
    if (isInWebview) {
      setTimeout(() => {
        sendEventToWebview(LOGOUT_WEBVIEW);
      }, 1000);
    } else {
      navigate('/');
    }
  };

  const renderActions = (): JSX.Element => {
    return (
      <ActionsWrapper>
        <Actions>
          {!hideBackButton && (
            <Button
              variant="tertiary"
              fill={isMobile}
              onClick={onCancel}
              data-testid="cancelButtonMySettings"
            >
              {t('Settings.Cancel')}
            </Button>
          )}
          <Button
            variant="accent"
            fill={isMobile}
            loading={isLoading}
            disabled={!isSubmitEnabled}
            onClick={onSaveChanges}
            data-testid="my-settings-save-button"
          >
            {t('Settings.Guardar')}
          </Button>
        </Actions>
      </ActionsWrapper>
    );
  };

  return (
    <Header>
      <HeaderContent>
        <TitleWrapper>
          {!hideBackButton && (
            <>
              <IconButton icon="CloseIcon" onClick={onGoBack} />
              <Title>{t('Modal.Tancar')}</Title>
            </>
          )}
          {isStatusExpired && (
            <>
              <IconButton icon="LogoutIcon" onClick={handleLogout} />
              <Title>{t('Cerrar sesión')}</Title>
            </>
          )}
        </TitleWrapper>
        {isMobile ? createPortal(renderActions(), drawerRoot) : renderActions()}
      </HeaderContent>
    </Header>
  );
}
